// Lightweight rich-text editor: contentEditable + a small formatting toolbar.
// Zero dependencies — document.execCommand is deprecated-but-universal and
// fine for bold/italic/underline/lists/links in an internal tool. Emits both
// HTML (what we send) and innerText (the text/plain MIME fallback + emptiness
// checks).

import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import {
  Bold,
  IndentDecrease,
  IndentIncrease,
  Italic,
  Link2,
  List,
  ListOrdered,
  RemoveFormatting,
  Underline,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn, normalizeHref } from '@/lib/utils';

// Tags we keep on paste. Everything else (font, table, o:p, mso junk, etc.) is
// unwrapped to its text; all attributes are dropped except safe link hrefs.
// The server sanitises the body again on send, so this is just for a clean,
// template-friendly paste — not the security boundary.
const PASTE_ALLOWED_TAGS = new Set([
  'A', 'B', 'STRONG', 'I', 'EM', 'U', 'S', 'STRIKE', 'BR', 'P', 'DIV', 'SPAN',
  'UL', 'OL', 'LI', 'BLOCKQUOTE', 'H1', 'H2', 'H3', 'H4', 'PRE', 'CODE',
]);

// A token is a live-linkable URL only when the WHOLE thing (after the last
// space) is URL-shaped — explicit scheme, or a www. host with a dot. Tokens
// with trailing punctuation are left for the send-time autolink pass, which
// trims punctuation correctly without fighting the caret.
const LIVE_URL_RE = /^(https?:\/\/\S+|www\.\S+\.\S+)$/i;

function sanitizePastedHtml(html: string): string {
  const tpl = document.createElement('template');
  tpl.innerHTML = html;
  tpl.content
    .querySelectorAll('script,style,meta,link,title,head,noscript,iframe,object,embed')
    .forEach((n) => n.remove());
  for (const el of Array.from(tpl.content.querySelectorAll('*'))) {
    if (!PASTE_ALLOWED_TAGS.has(el.tagName)) {
      const parent = el.parentNode;
      if (parent) {
        while (el.firstChild) parent.insertBefore(el.firstChild, el);
        parent.removeChild(el);
      }
      continue;
    }
    for (const name of el.getAttributeNames()) {
      if (el.tagName === 'A' && name === 'href') {
        if (/^\s*javascript:/i.test(el.getAttribute('href') ?? '')) el.removeAttribute('href');
      } else {
        el.removeAttribute(name);
      }
    }
  }
  return tpl.innerHTML;
}

const BLOCK_TAGS = new Set(['P', 'DIV', 'UL', 'OL', 'LI', 'BLOCKQUOTE', 'H1', 'H2', 'H3', 'H4', 'PRE']);

function placeCaret(node: Node, atStart: boolean): void {
  const sel = window.getSelection();
  if (!sel) return;
  const r = document.createRange();
  if (atStart) r.setStart(node, 0);
  else r.setStartAfter(node);
  r.collapse(true);
  sel.removeAllRanges();
  sel.addRange(r);
}

/** Insert HTML at the caret WITHOUT execCommand('insertHTML'), which merges
 *  block content into the paragraph the caret sits in — lists, headings and
 *  paragraphs come out as one flat run with only bold/italic surviving. Here
 *  block-level fragments split the current block and land between the
 *  halves; inline-only fragments go in at the caret as before. Pasting a list
 *  while inside a list item splices the items in as siblings. */
function insertHtmlAtCaret(root: HTMLElement, html: string): void {
  const tpl = document.createElement('template');
  tpl.innerHTML = html;
  const frag = tpl.content;
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0 || !root.contains(sel.getRangeAt(0).startContainer)) {
    root.appendChild(frag);
    return;
  }
  const range = sel.getRangeAt(0);
  range.deleteContents();
  const hasBlock = Array.from(frag.childNodes).some(
    (n) => n instanceof HTMLElement && (BLOCK_TAGS.has(n.tagName) || n.querySelector('ul,ol,p,div,h1,h2,h3,h4,blockquote,pre')),
  );
  if (!hasBlock) {
    const last = frag.lastChild;
    range.insertNode(frag);
    if (last) placeCaret(last, false);
    return;
  }
  // Closest block the caret is in (stop at the editor root).
  let block: HTMLElement | null =
    range.startContainer instanceof HTMLElement ? range.startContainer : range.startContainer.parentElement;
  while (block && block !== root && !BLOCK_TAGS.has(block.tagName)) block = block.parentElement;
  if (!block || block === root) {
    const last = frag.lastChild;
    range.insertNode(frag);
    if (last) placeCaret(last, false);
    return;
  }
  // Inside a list item and pasting a list: splice the items in as siblings.
  if (block.tagName === 'LI') {
    const items: Node[] = [];
    for (const n of Array.from(frag.childNodes)) {
      if (n instanceof HTMLElement && (n.tagName === 'UL' || n.tagName === 'OL')) items.push(...Array.from(n.childNodes));
      else items.push(n);
    }
    let ref: Node = block;
    for (const n of items) {
      if (n instanceof HTMLElement && n.tagName !== 'LI') {
        const li = document.createElement('li');
        li.appendChild(n);
        (ref as ChildNode).after(li);
        ref = li;
      } else if (n.nodeType === Node.TEXT_NODE && !n.textContent?.trim()) {
        continue;
      } else {
        (ref as ChildNode).after(n);
        ref = n;
      }
    }
    if (!block.textContent?.trim() && !block.querySelector('img')) block.remove();
    placeCaret(ref, false);
    return;
  }
  // Split the block at the caret: head stays, tail becomes a clone after the
  // pasted blocks.
  const tailRange = document.createRange();
  tailRange.setStart(range.startContainer, range.startOffset);
  tailRange.setEnd(block, block.childNodes.length);
  const tailFrag = tailRange.extractContents();
  const tail = block.cloneNode(false) as HTMLElement;
  tail.appendChild(tailFrag);
  let ref: ChildNode = block;
  for (const n of Array.from(frag.childNodes)) {
    if (n.nodeType === Node.TEXT_NODE && !n.textContent?.trim()) continue;
    ref.after(n);
    ref = n as ChildNode;
  }
  const tailHasContent = !!tail.textContent?.trim() || !!tail.querySelector('img,br');
  if (tailHasContent) {
    ref.after(tail);
    placeCaret(tail, true);
  } else {
    placeCaret(ref, false);
  }
  if (!block.textContent?.trim() && !block.querySelector('img,br')) block.remove();
}

const MD_LINE_RE = /^(\s*)([-*•]|\d+[.)])\s+/;

/** Does this plain text look like Markdown worth rendering? (The Copy buttons
 *  in Claude / ChatGPT hand over Markdown, not HTML.) */
function looksLikeMarkdown(text: string): boolean {
  const lines = text.split(/\r?\n/);
  const listy = lines.filter((l) => MD_LINE_RE.test(l)).length;
  return listy >= 2 || /\*\*[^*\n]+\*\*/.test(text) || /^#{1,4}\s+\S/m.test(text) || /`[^`\n]+`/.test(text);
}

function mdInline(src: string): string {
  let h = escapeHtml(src);
  h = h.replace(/`([^`]+)`/g, '<code>$1</code>');
  h = h.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  h = h.replace(/(^|[^*\w])\*([^*\n]+)\*(?!\w)/g, '$1<em>$2</em>');
  h = h.replace(/(^|[^_\w])_([^_\n]+)_(?!\w)/g, '$1<em>$2</em>');
  h = h.replace(/\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/g, '<a href="$2">$1</a>');
  return h;
}

/** Minimal Markdown → HTML: headings, bullet/numbered lists (one nesting
 *  level by indent), paragraphs, bold/italic/code/links. Enough for chat
 *  output; anything odd falls out as plain paragraphs. */
function markdownToHtml(text: string): string {
  const lines = text.replace(/\r\n?/g, '\n').split('\n');
  const out: string[] = [];
  let para: string[] = [];
  const listStack: { tag: 'ul' | 'ol'; indent: number }[] = [];
  const flushPara = () => {
    if (para.length) {
      out.push(`<p>${para.map(mdInline).join('<br>')}</p>`);
      para = [];
    }
  };
  const closeLists = (toIndent = -1) => {
    while (listStack.length && listStack[listStack.length - 1]!.indent > toIndent) {
      out.push(`</li></${listStack.pop()!.tag}>`);
    }
  };
  for (const raw of lines) {
    const line = raw.replace(/\t/g, '    ');
    const m = line.match(MD_LINE_RE);
    if (m) {
      flushPara();
      const indent = m[1]!.length;
      const tag: 'ul' | 'ol' = /\d/.test(m[2]!) ? 'ol' : 'ul';
      const content = mdInline(line.slice(m[0].length));
      const top = listStack[listStack.length - 1];
      if (!top || indent > top.indent) {
        out.push(`<${tag}><li>${content}`);
        listStack.push({ tag, indent });
      } else {
        closeLists(indent);
        const cur = listStack[listStack.length - 1];
        if (cur && cur.indent === indent) {
          out.push(`</li><li>${content}`);
        } else {
          out.push(`<${tag}><li>${content}`);
          listStack.push({ tag, indent });
        }
      }
      continue;
    }
    const h = line.match(/^(#{1,4})\s+(.*)$/);
    if (h) {
      flushPara();
      closeLists();
      out.push(`<h${h[1]!.length}>${mdInline(h[2]!)}</h${h[1]!.length}>`);
      continue;
    }
    if (!line.trim()) {
      flushPara();
      closeLists();
      continue;
    }
    if (listStack.length && /^\s+/.test(line)) {
      // Continuation line inside a list item.
      out.push(`<br>${mdInline(line.trim())}`);
      continue;
    }
    closeLists();
    para.push(line);
  }
  flushPara();
  closeLists();
  return out.join('');
}

/** The anchor element a node sits inside, if any. */
function anchorAt(node: Node | null): HTMLAnchorElement | null {
  const el = node instanceof Element ? node : (node?.parentElement ?? null);
  return el ? (el.closest('a') as HTMLAnchorElement | null) : null;
}

export interface RichTextValue {
  html: string;
  text: string;
}

export interface RichTextEditorHandle {
  focus: () => void;
  /** Replace content (used for draft hydration). */
  setHtml: (html: string) => void;
  /** Clear the editor. */
  clear: () => void;
  /** Insert image files inline at the caret (paste/drop/attach-as-inline).
   *  Large rasters are downscaled client-side; the send path converts the
   *  resulting data: URIs into proper cid: inline MIME parts. */
  insertImageFiles: (files: File[]) => void;
  /** Insert an HTML fragment at the caret (canned-response templates). */
  insertHtml: (html: string) => void;
}

// --- Inline images -----------------------------------------------------------

/** Raster types we can safely downscale via canvas. GIFs are inlined as-is
 *  (canvas would freeze the animation); everything else is rejected. */
const INLINEABLE_IMAGE = /^image\/(png|jpe?g|webp|gif)$/i;
const MAX_INLINE_SOURCE_BYTES = 25 * 1024 * 1024; // absurd-input guard
const MAX_GIF_INLINE_BYTES = 4 * 1024 * 1024; // GIFs can't be downscaled — cap them
const MAX_EDGE_PX = 1400; // plenty for email; keeps data URIs sane

/** File → data: URI, downscaling big rasters so a phone photo doesn't become a
 *  10 MB email. Returns null when the file isn't an inlineable image. */
async function fileToInlineDataUri(file: File): Promise<string | null> {
  if (!INLINEABLE_IMAGE.test(file.type) || file.size > MAX_INLINE_SOURCE_BYTES) return null;
  const rawUri = await new Promise<string>((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(r.error);
    r.readAsDataURL(file);
  });
  if (/^image\/gif$/i.test(file.type)) {
    return file.size <= MAX_GIF_INLINE_BYTES ? rawUri : null;
  }
  const img = await new Promise<HTMLImageElement | null>((resolve) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = () => resolve(null);
    i.src = rawUri;
  });
  if (!img) return null;
  const scale = Math.min(1, MAX_EDGE_PX / Math.max(img.naturalWidth, img.naturalHeight));
  // Small enough already → keep the original bytes (no recompression pass).
  // 600 KB keeps even a multi-image email comfortably inside body limits.
  if (scale === 1 && file.size <= 600 * 1024) return rawUri;
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(img.naturalWidth * scale));
  canvas.height = Math.max(1, Math.round(img.naturalHeight * scale));
  const ctx = canvas.getContext('2d');
  if (!ctx) return rawUri;
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  // PNGs with few colours (screenshots) stay PNG; photos re-encode smaller as
  // JPEG. Pick whichever encoding is smaller.
  const asJpeg = canvas.toDataURL('image/jpeg', 0.85);
  const asPng = file.type === 'image/png' ? canvas.toDataURL('image/png') : null;
  return asPng && asPng.length < asJpeg.length ? asPng : asJpeg;
}

interface RichTextEditorProps {
  /** Initial content only — the editor is uncontrolled while typing to keep
   *  the caret stable. Use the ref's setHtml for external resets. */
  defaultHtml?: string;
  onChange: (v: RichTextValue) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  /** Tailwind min-height utility for the editing surface. */
  minHeightClass?: string;
  onMetaEnter?: () => void;
}

export const RichTextEditor = forwardRef<RichTextEditorHandle, RichTextEditorProps>(
  function RichTextEditor(
    { defaultHtml, onChange, placeholder, disabled, className, minHeightClass = 'min-h-[72px]', onMetaEnter },
    ref,
  ) {
    const editorRef = useRef<HTMLDivElement | null>(null);
    // Link editor: an inline second toolbar row for creating / editing a link.
    const [linkOpen, setLinkOpen] = useState(false);
    const [linkUrl, setLinkUrl] = useState('');
    const [editingLink, setEditingLink] = useState(false);
    // The editor selection at the moment the link editor opened — restored on
    // apply, since focusing the URL input drops the live selection.
    const savedRange = useRef<Range | null>(null);
    const linkInputRef = useRef<HTMLInputElement | null>(null);

    useImperativeHandle(ref, () => ({
      focus: () => editorRef.current?.focus(),
      setHtml: (html: string) => {
        if (editorRef.current) {
          editorRef.current.innerHTML = html;
          emit();
        }
      },
      clear: () => {
        if (editorRef.current) {
          editorRef.current.innerHTML = '';
          emit();
        }
      },
      insertImageFiles: (files: File[]) => void insertImages(files),
      insertHtml: (html: string) => {
        const el = editorRef.current;
        if (!el) return;
        el.focus();
        insertHtmlAtCaret(el, html);
        emit();
      },
    }));

    /** Inline every insertable image at the caret, in file order. Sequential
     *  awaits keep the insertion order stable. */
    async function insertImages(files: File[]) {
      const el = editorRef.current;
      if (!el) return;
      el.focus();
      for (const file of files) {
        const uri = await fileToInlineDataUri(file).catch(() => null);
        if (!uri) continue;
        document.execCommand(
          'insertHTML',
          false,
          `<img src="${uri}" alt="${escapeAttr(file.name)}" style="max-width:100%;height:auto"><br>`,
        );
      }
      emit();
    }

    // Set initial content once.
    useEffect(() => {
      if (editorRef.current && defaultHtml && editorRef.current.innerHTML === '') {
        editorRef.current.innerHTML = defaultHtml;
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      if (linkOpen) linkInputRef.current?.focus();
    }, [linkOpen]);

    function emit() {
      const el = editorRef.current;
      if (!el) return;
      onChange({ html: el.innerHTML, text: el.innerText });
    }

    function exec(command: string) {
      editorRef.current?.focus();
      document.execCommand(command);
      emit();
    }

    /** Is the caret currently inside a list item of THIS editor? */
    function caretInListItem(): boolean {
      const sel = window.getSelection();
      let node: Node | null = sel?.anchorNode ?? null;
      while (node && node !== editorRef.current) {
        if (node.nodeType === Node.ELEMENT_NODE && (node as Element).tagName === 'LI') return true;
        node = node.parentNode;
      }
      return false;
    }

    /** Current selection range, but only if it lives inside this editor. */
    function editorRange(): Range | null {
      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0) return null;
      const range = sel.getRangeAt(0);
      const el = editorRef.current;
      return el && el.contains(range.commonAncestorContainer) ? range : null;
    }

    // ⌘K / Link button: open the inline link editor. If the caret is inside an
    // existing link, prefill its URL and target the whole anchor so Apply edits
    // it (and Remove unlinks it).
    function openLinkEditor() {
      editorRef.current?.focus();
      const range = editorRange();
      const existing = range ? anchorAt(range.startContainer) : null;
      if (existing) {
        const ar = document.createRange();
        ar.selectNode(existing);
        savedRange.current = ar;
        setLinkUrl(existing.getAttribute('href') ?? '');
        setEditingLink(true);
      } else {
        savedRange.current = range ? range.cloneRange() : null;
        setLinkUrl('');
        setEditingLink(false);
      }
      setLinkOpen(true);
    }

    function closeLinkEditor() {
      setLinkOpen(false);
      setLinkUrl('');
      setEditingLink(false);
      savedRange.current = null;
    }

    function restoreSelection() {
      const sel = window.getSelection();
      const range = savedRange.current;
      if (sel && range) {
        sel.removeAllRanges();
        sel.addRange(range);
      }
    }

    function applyLink() {
      const url = linkUrl.trim();
      editorRef.current?.focus();
      restoreSelection();
      const range = savedRange.current;
      if (!url) {
        document.execCommand('unlink');
      } else if (range && !range.collapsed) {
        // Selected text (or an existing anchor) → (re)wrap with the new href.
        document.execCommand('createLink', false, normalizeHref(url));
      } else {
        // No selection → drop the URL in as its own link.
        document.execCommand(
          'insertHTML',
          false,
          `<a href="${escapeAttr(normalizeHref(url))}">${escapeHtml(url)}</a>`,
        );
      }
      closeLinkEditor();
      emit();
    }

    function removeLink() {
      editorRef.current?.focus();
      restoreSelection();
      document.execCommand('unlink');
      closeLinkEditor();
      emit();
    }

    // Auto-link the URL just finished when the user types a space or Enter.
    function maybeLinkifyBeforeCaret() {
      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0 || !sel.isCollapsed) return;
      const node = sel.anchorNode;
      if (!node || node.nodeType !== Node.TEXT_NODE || anchorAt(node)) return;
      const offset = sel.anchorOffset;
      const before = (node.nodeValue ?? '').slice(0, offset);
      const m = before.match(/(\S+)$/);
      const token = m?.[1];
      if (!token || !LIVE_URL_RE.test(token)) return;
      const start = offset - token.length;
      const range = document.createRange();
      range.setStart(node, start);
      range.setEnd(node, offset);
      const a = document.createElement('a');
      a.setAttribute('href', normalizeHref(token));
      try {
        range.surroundContents(a);
      } catch {
        return; // range straddled an element boundary — leave it for send-time
      }
      // Drop the caret just after the new anchor so the space/Enter that
      // triggered this lands normally after the link.
      const after = document.createRange();
      after.setStartAfter(a);
      after.collapse(true);
      sel.removeAllRanges();
      sel.addRange(after);
      emit();
    }

    const tools: { icon: typeof Bold; command: string; label: string }[] = [
      { icon: Bold, command: 'bold', label: 'Bold (⌘B)' },
      { icon: Italic, command: 'italic', label: 'Italic (⌘I)' },
      { icon: Underline, command: 'underline', label: 'Underline (⌘U)' },
      { icon: List, command: 'insertUnorderedList', label: 'Bullet list' },
      { icon: ListOrdered, command: 'insertOrderedList', label: 'Numbered list' },
      { icon: IndentIncrease, command: 'indent', label: 'Indent (Tab in a list)' },
      { icon: IndentDecrease, command: 'outdent', label: 'Outdent (Shift+Tab)' },
      { icon: RemoveFormatting, command: 'removeFormat', label: 'Clear formatting' },
    ];

    return (
      <div className={cn('rounded-md border bg-background', className)}>
        <div className="flex items-center gap-0.5 border-b px-1.5 py-1">
          {tools.map(({ icon: Icon, command, label }) => (
            <Tooltip key={command}>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="size-6"
                  disabled={disabled}
                  // onMouseDown+preventDefault keeps the selection in the
                  // editor — a click would blur it and drop the range.
                  onMouseDown={(e) => {
                    e.preventDefault();
                    exec(command);
                  }}
                  aria-label={label}
                >
                  <Icon className="size-3.5" strokeWidth={2} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{label}</TooltipContent>
            </Tooltip>
          ))}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-6"
                disabled={disabled}
                onMouseDown={(e) => {
                  e.preventDefault();
                  openLinkEditor();
                }}
                aria-label="Link (⌘K)"
              >
                <Link2 className="size-3.5" strokeWidth={2} />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Link (⌘K)</TooltipContent>
          </Tooltip>
        </div>

        {linkOpen && (
          <div className="flex items-center gap-1.5 border-b bg-muted/30 px-1.5 py-1.5">
            <input
              ref={linkInputRef}
              type="text"
              inputMode="url"
              placeholder="Paste or type a link…"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  applyLink();
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  closeLinkEditor();
                  editorRef.current?.focus();
                }
              }}
              className="h-7 flex-1 rounded-md border bg-background px-2 text-sm outline-none focus:ring-1 focus:ring-ring"
            />
            <Button type="button" size="xs" onMouseDown={(e) => e.preventDefault()} onClick={applyLink}>
              {editingLink ? 'Update' : 'Add'}
            </Button>
            {editingLink && (
              <Button
                type="button"
                size="xs"
                variant="outline"
                onMouseDown={(e) => e.preventDefault()}
                onClick={removeLink}
              >
                Remove
              </Button>
            )}
            <Button
              type="button"
              size="xs"
              variant="ghost"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                closeLinkEditor();
                editorRef.current?.focus();
              }}
            >
              Cancel
            </Button>
          </div>
        )}

        <div
          ref={editorRef}
          contentEditable={!disabled}
          suppressContentEditableWarning
          role="textbox"
          aria-multiline="true"
          data-placeholder={placeholder ?? ''}
          onInput={emit}
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
              e.preventDefault();
              openLinkEditor();
              return;
            }
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              maybeLinkifyBeforeCaret();
              onMetaEnter?.();
              return;
            }
            // Tab / Shift+Tab inside a list nests / un-nests the item — the
            // behaviour everyone expects from bullets. Outside a list, Tab
            // keeps its normal move-focus meaning.
            if (e.key === 'Tab' && caretInListItem()) {
              e.preventDefault();
              document.execCommand(e.shiftKey ? 'outdent' : 'indent');
              emit();
              return;
            }
            if (e.key === ' ' || e.key === 'Enter') {
              maybeLinkifyBeforeCaret();
            }
          }}
          onPaste={(e) => {
            // Pasted images (screenshots!) go inline at the caret.
            const imgItems = Array.from(e.clipboardData.items ?? []).filter(
              (it) => it.kind === 'file' && INLINEABLE_IMAGE.test(it.type),
            );
            if (imgItems.length > 0) {
              e.preventDefault();
              const files = imgItems
                .map((it) => it.getAsFile())
                .filter((f): f is File => !!f);
              void insertImages(files);
              return;
            }
            // Keep basic formatting (bold/italic/lists/links/headings) but
            // strip styles, colours, fonts and other wild markup that fights
            // the email template. Fall back to plain text if there's no usable
            // HTML on the clipboard.
            e.preventDefault();
            const root = editorRef.current;
            const html = e.clipboardData.getData('text/html');
            const clean = html ? sanitizePastedHtml(html) : '';
            if (root && clean.replace(/<br\s*\/?>(\s|&nbsp;)*/gi, '').trim()) {
              insertHtmlAtCaret(root, clean);
            } else {
              const plain = e.clipboardData.getData('text/plain');
              // A pasted bare URL becomes a link straight away; Markdown (the
              // Copy buttons in Claude / ChatGPT give Markdown, not HTML) is
              // rendered; everything else is inserted as-is.
              if (root && LIVE_URL_RE.test(plain.trim())) {
                insertHtmlAtCaret(
                  root,
                  `<a href="${escapeAttr(normalizeHref(plain.trim()))}">${escapeHtml(plain.trim())}</a>`,
                );
              } else if (root && looksLikeMarkdown(plain)) {
                insertHtmlAtCaret(root, markdownToHtml(plain));
              } else {
                document.execCommand('insertText', false, plain);
              }
            }
            emit();
          }}
          className={cn(
            'wharf-rte block w-full px-3 py-2 text-sm outline-none',
            minHeightClass,
            disabled && 'pointer-events-none opacity-60',
          )}
        />
      </div>
    );
  },
);

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function escapeAttr(s: string): string {
  return escapeHtml(s).replace(/"/g, '&quot;');
}
